import { useState, useEffect, memo } from 'react';
import { Grid, Users, ChevronsLeft, User, Clock, Award, Loader, ChevronDown, ChevronUp, Timer, TrendingUp, Zap, List, RefreshCw, Heart, XCircle, ToggleLeft, ToggleRight, ShoppingCart, ArrowRight, Rocket, Shield } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";

const CASE_CONFIG = {
    1: { name: 'Кейс 1', cost: 100, exit: 300, gradient: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
    2: { name: 'Кейс 2', cost: 300, exit: 900, gradient: 'from-purple-500 to-violet-600', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
    3: { name: 'Кейс 3', cost: 900, exit: 2700, gradient: 'from-orange-500 to-red-600', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
};

const formatDateWithMicroseconds = (dateString) => {
    if (!dateString) return 'Н/Д';
    const parts = dateString.split(' ');
    if (parts.length === 2) { const [d, t] = parts; const [y, m, dd] = d.split('-'); const [time, us] = t.split('.'); return `${dd}.${m}.${y} ${time}.${us || '000000'}`; }
    return dateString;
};

const MicrosecondsDisplay = memo(({ dateString, short = false }) => {
    const formatted = formatDateWithMicroseconds(dateString);
    const parts = formatted.split('.');
    if (parts.length === 4) {
        if (short) return (<div className="text-[10px] leading-tight"><div className="font-mono text-gray-600">{parts[0]}.{parts[1]}.{parts[2]}</div><div className="font-mono text-orange-500 font-semibold">{parts[3].substring(0,2)}:{parts[3].substring(2,4)} .{parts[3]}</div></div>);
        return (<span className="font-mono text-xs sm:text-sm">{parts[0]}.{parts[1]}.{parts[2]} {parts[3].substring(0,2)}:{parts[3].substring(2,4)}:{parts[3].substring(4,6)}<span className="text-orange-500 font-semibold">.{parts[3]}</span></span>);
    }
    return <span className="font-mono text-xs sm:text-sm">{formatted}</span>;
});

const PositionBar = memo(({ occupied, total=3 }) => (
    <div className="flex gap-1">{Array.from({length:total},(_,i)=>(
        <div key={i} className={`h-2 flex-1 rounded-full transition-all ${i<occupied?'bg-orange-500':'bg-gray-200'}`}/>
    ))}</div>
));

const QueueTable = memo(({ data, level }) => {
    const cfg = CASE_CONFIG[level] || CASE_CONFIG[1];
    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className={`grid grid-cols-12 gap-1 px-3 py-2 ${cfg.bg} text-[10px] sm:text-xs font-semibold ${cfg.text} border-b ${cfg.border}`}>
                <div className="col-span-1 text-center">#</div><div className="col-span-3">Логин</div><div className="col-span-1 text-center">Кр.</div><div className="col-span-2 text-center">Заполн.</div><div className="col-span-5 text-right">Время активации</div>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
                {data.map((item, index) => (
                    <div key={item.id} className={`grid grid-cols-12 gap-1 px-3 py-2 text-xs border-b border-gray-50 items-center ${item.isCurrentUser?`${cfg.bg} font-medium`:index%2===0?'bg-white':'bg-gray-50/50'}`}>
                        <div className="col-span-1 text-center text-gray-400">{item.queue_position}</div>
                        <div className={`col-span-3 ${item.isCurrentUser?cfg.text:'text-gray-600'}`}>{item.login}{item.isCurrentUser&&' ⭐'}</div>
                        <div className="col-span-1 text-center text-gray-400">{item.round}</div>
                        <div className="col-span-2 text-center"><div className="flex gap-0.5 justify-center">{Array.from({length:3},(_,i)=>(<div key={i} className={`h-1.5 w-3 rounded-full ${i<item.occupied_count?cfg.dot:'bg-gray-200'}`}/>))}</div></div>
                        <div className="col-span-5 text-right"><MicrosecondsDisplay dateString={item.activated_at||item.purchase_timestamp} short/></div>
                    </div>))}
            </div>
        </div>
    );
});

const PendingQueueTable = memo(({ data }) => (
    <div className="bg-white border border-amber-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-amber-50 text-[10px] sm:text-xs font-semibold text-amber-700 border-b border-amber-100">
            <div className="col-span-1 text-center">#</div><div className="col-span-3">Логин</div><div className="col-span-2 text-center">Источник</div><div className="col-span-3 text-center">Ожидание</div><div className="col-span-3 text-right">Время</div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
            {data.map((item, index) => (
                <div key={item.id} className={`grid grid-cols-12 gap-1 px-3 py-2 text-xs border-b border-gray-50 items-center ${item.isCurrentUser?'bg-amber-50 font-medium':index%2===0?'bg-white':'bg-gray-50/50'}`}>
                    <div className="col-span-1 text-center text-gray-400">{item.queue_position}</div>
                    <div className={`col-span-3 ${item.isCurrentUser?'text-amber-600':'text-gray-600'}`}>{item.login}{item.isCurrentUser&&' ⭐'}</div>
                    <div className="col-span-2 text-center text-[10px] text-gray-400">{item.source==='charity'?'Благотв.':item.source==='case_upgrade'?'Переход':item.source==='purchase'?'Покупка':item.source}</div>
                    <div className="col-span-3 text-center"><span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{item.waiting_time}</span></div>
                    <div className="col-span-3 text-right"><MicrosecondsDisplay dateString={item.activation_time||item.created_at} short/></div>
                </div>))}
        </div>
    </div>
));

const CharityQueueList = memo(({ data }) => (
    <div className="mt-2 border border-pink-100 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-1 px-3 py-1.5 bg-pink-50 text-[10px] font-semibold text-pink-700">
            <div className="col-span-1">#</div><div className="col-span-5">Логин</div><div className="col-span-3">Ожидание</div><div className="col-span-3 text-right">Записан</div>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
            {data.map(item => (
                <div key={item.id} className={`grid grid-cols-12 gap-1 px-3 py-1.5 text-[10px] border-b border-gray-50 ${item.isCurrentUser?'bg-pink-50 font-medium':''}`}>
                    <div className="col-span-1 text-gray-400">{item.queue_position}</div>
                    <div className={`col-span-5 ${item.isCurrentUser?'text-pink-600':'text-gray-600'}`}>{item.login}{item.isCurrentUser&&' ⭐'}</div>
                    <div className="col-span-3 text-gray-400">{item.waiting_time}</div>
                    <div className="col-span-3 text-right text-gray-400">{item.created_at?.split(' ')[0]}</div>
                </div>))}
        </div>
    </div>
));

const LightTurboKeysCluster = ({ onBack }) => {
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [caseQueues, setCaseQueues] = useState({ 1: [], 2: [], 3: [] });
    const [fullMainQueues, setFullMainQueues] = useState({ 1: [], 2: [], 3: [] });
    const [fullPendingQueues, setFullPendingQueues] = useState({ 1: [], 2: [], 3: [] });
    const [fullCharityQueue, setFullCharityQueue] = useState([]);
    const [userPlaces, setUserPlaces] = useState([]);
    const [pendingPlaces, setPendingPlaces] = useState([]);
    const [charityQueue, setCharityQueue] = useState({ is_in_queue: false, total_waiting: 0, can_join: false });
    const [statistics, setStatistics] = useState({ total_in_queue: 0, user_places: 0, earned: 0, case_breakdown: {}, charity_queue_size: 0, place_cost: 100, completed_places: 0, pending_places: 0 });
    const [canPurchase, setCanPurchase] = useState(true);
    const [purchaseInfo, setPurchaseInfo] = useState(null);
    const [purchaseCountdown, setPurchaseCountdown] = useState(0);
    const [purchaseStatus, setPurchaseStatus] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingTime, setProcessingTime] = useState(0);
    const [notification, setNotification] = useState(null);
    const [activeCase, setActiveCase] = useState(1);
    const [autoPurchase, setAutoPurchase] = useState(false);
    const [showFullQueue, setShowFullQueue] = useState({ 1: false, 2: false, 3: false });
    const [showPendingQueue, setShowPendingQueue] = useState({ 1: false, 2: false, 3: false });
    const [showCharityList, setShowCharityList] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [totalReady, setTotalReady] = useState(0);
    const [markingReady, setMarkingReady] = useState(false);
    const [isCharityReady, setIsCharityReady] = useState(false);
    const [totalCharityReady, setTotalCharityReady] = useState(0);
    const [markingCharityReady, setMarkingCharityReady] = useState(false);
    const [reserveFund, setReserveFund] = useState(0);

    const navigate = useNavigate();
    const API_BASE = 'https://api.light-platform.com/api/v1/light-keys-turbo';
    const getAuthHeaders = () => { const t = localStorage.getItem('auth_token'); return t ? { Authorization: `Bearer ${t}` } : {}; };

    const formatCountdown = (s) => {
        if (s <= 0) return { days: 0, hours: '00', mins: '00', secs: '00' };
        return { days: Math.floor(s/86400), hours: Math.floor((s%86400)/3600).toString().padStart(2,'0'), mins: Math.floor((s%3600)/60).toString().padStart(2,'0'), secs: (s%60).toString().padStart(2,'0') };
    };
    const countdown = formatCountdown(purchaseCountdown);

    useEffect(() => { if (!isProcessing || !purchaseStatus?.clicked_at) { setProcessingTime(0); return; } const st = new Date(purchaseStatus.clicked_at).getTime(); const i = setInterval(() => setProcessingTime(Math.floor((Date.now()-st)/1000)), 1000); return () => clearInterval(i); }, [isProcessing, purchaseStatus?.clicked_at]);
    useEffect(() => { if (purchaseCountdown <= 0) return; const i = setInterval(() => { setPurchaseCountdown(p => { if (p<=1){clearInterval(i);setCanPurchase(true);return 0;} return p-1; }); }, 1000); return () => clearInterval(i); }, [purchaseCountdown]);
    useEffect(() => { if (notification) { const t = setTimeout(() => setNotification(null), 5000); return () => clearTimeout(t); } }, [notification]);

    const [lastFetchTime, setLastFetchTime] = useState(0);

    const fetchData = async (showLoader = true) => {
        // Защита от спама — не чаще 10 сек
        const now = Date.now();
        if (!showLoader && now - lastFetchTime < 10000) return;
        setLastFetchTime(now);
        try {
            if (showLoader) setLoading(true);
            const qR = await axios.get(`${API_BASE}/queue-data`, { headers: getAuthHeaders() }).catch(() => null);

            if (!qR || !qR.data?.success) {
                // Сервер не ответил — НЕ выкидываем, просто ждём
                console.warn('Turbo Keys: сервер не ответил, повторим позже');
                return;
            }

            const d = qR.data.data;
            setCaseQueues(d.case_queues || {1:[],2:[],3:[]});
            setFullMainQueues(d.full_main_queues || {1:[],2:[],3:[]});
            setFullPendingQueues(d.full_pending_queues || {1:[],2:[],3:[]});
            setFullCharityQueue(d.full_charity_queue || []);
            setUserPlaces(d.user_places || []);
            setPendingPlaces(d.pending_places || []);
            setCharityQueue(d.charity_queue || {is_in_queue:false,total_waiting:0,can_join:false});
            setStatistics(d.statistics || {});
            setAutoPurchase(d.auto_purchase || false);
            setReserveFund(Number(d.reserve_fund_balance ?? 0));

            // can_purchase (из того же ответа)
            if (d.can_purchase_data) {
                const p = d.can_purchase_data;
                setCanPurchase(p.can_purchase);
                setPurchaseInfo(p);
                setPurchaseCountdown(p.seconds_until_available > 0 ? p.seconds_until_available : 0);
                setLastSyncTime(p.server_time || new Date().toISOString());
                if (p.reason === 'has_pending_click') {
                    setIsProcessing(true);
                    setPurchaseStatus({ clicked_at: p.clicked_at, status: 'pending' });
                }
            }

            // purchase_status (из того же ответа)
            if (d.purchase_status?.has_click && d.purchase_status.status === 'pending') {
                setIsProcessing(true);
                setPurchaseStatus(d.purchase_status);
            }

            // ready (из того же ответа)
            if (d.ready_data) {
                setTotalReady(d.ready_data.total_ready || 0);
                setIsReady(d.ready_data.is_ready || false);
                setTotalCharityReady(d.ready_data.total_charity_ready || 0);
                setIsCharityReady(d.ready_data.is_charity_ready || false);
            }
        } catch(e) { console.error('Fetch error:', e); } finally { setLoading(false); }
    };
    useEffect(() => { fetchData(); }, []);

    // Я готов участвовать
    const handleMarkReady = async () => {
        if (markingReady || isReady) return;
        try {
            setMarkingReady(true);
            const r = await axios.post(`${API_BASE}/mark-ready`, {}, { headers: getAuthHeaders() });
            if (r.data.success) {
                setIsReady(true);
                setIsCharityReady(false); // убираем из благотворительной
                if (r.data.data?.total_ready) setTotalReady(r.data.data.total_ready);
                setNotification({ type: 'success', message: r.data.message });
            } else {
                setNotification({ type: 'error', message: r.data.message });
            }
        } catch (e) {
            setNotification({ type: 'error', message: e.response?.data?.message || 'Ошибка' });
        } finally {
            setMarkingReady(false);
        }
    };

    // Жду благотворительность
    const handleMarkCharityReady = async () => {
        if (markingCharityReady || isCharityReady || isReady) return;
        try {
            setMarkingCharityReady(true);
            const r = await axios.post(`${API_BASE}/mark-charity-ready`, {}, { headers: getAuthHeaders() });
            if (r.data.success) {
                setIsCharityReady(true);
                if (r.data.data?.total_charity_ready) setTotalCharityReady(r.data.data.total_charity_ready);
                setNotification({ type: 'success', message: r.data.message });
            } else {
                setNotification({ type: 'error', message: r.data.message });
            }
        } catch (e) {
            setNotification({ type: 'error', message: e.response?.data?.message || 'Ошибка' });
        } finally {
            setMarkingCharityReady(false);
        }
    };

    // Синхронизация таймера с сервером
    const syncTimer = async () => {
        if (syncing) return;
        try {
            setSyncing(true);
            await fetchData(false);
        } catch (e) {
            console.error('Sync error:', e);
        } finally {
            setSyncing(false);
        }
    };

    const handlePurchase = async () => {
        if (purchasing || !canPurchase || isProcessing) return;
        try { setPurchasing(true); const r = await axios.post(`${API_BASE}/purchase`, {}, {headers:getAuthHeaders()}); if(r.data.success){setNotification({type:'success',message:r.data.message});setPurchaseStatus(r.data.data);setIsProcessing(true);startPolling();}else{setNotification({type:'error',message:r.data.message});} } catch(e){setNotification({type:'error',message:e.response?.data?.message||'Ошибка'});} finally{setPurchasing(false);}
    };
    const startPolling = () => {
        const pi = setInterval(async () => {
            try {
                const r = await axios.get(`${API_BASE}/queue-data`, { headers: getAuthHeaders() }).catch(() => null);
                if (!r || !r.data?.success) return;
                const ps = r.data.data.purchase_status;
                if (!ps?.has_click || ps.status !== 'pending') {
                    clearInterval(pi);
                    setIsProcessing(false);
                    setPurchaseStatus(null);
                    setNotification({ type: 'success', message: 'Покупка обработана!' });
                    fetchData(false);
                }
            } catch(e) {}
        }, 15000);
        setTimeout(() => { clearInterval(pi); setIsProcessing(false); }, 300000);
    };
    const [joiningCharity, setJoiningCharity] = useState(false);

    const handleJoinCharity = async () => { if(joiningCharity)return; try{setJoiningCharity(true);const r=await axios.post(`${API_BASE}/charity/join`,{},{headers:getAuthHeaders()});setNotification({type:r.data.success?'success':'error',message:r.data.message});if(r.data.success)fetchData(false);}catch(e){setNotification({type:'error',message:'Ошибка'});}finally{setJoiningCharity(false);} };
    const [decliningCharity, setDecliningCharity] = useState(false);

    const handleDeclineCharity = async () => { if(decliningCharity)return; try{setDecliningCharity(true);const r=await axios.post(`${API_BASE}/charity/decline`,{},{headers:getAuthHeaders()});setNotification({type:r.data.success?'success':'error',message:r.data.message});if(r.data.success)fetchData(false);}catch(e){setNotification({type:'error',message:'Ошибка'});}finally{setDecliningCharity(false);} };
    const handleToggleAuto = async () => { try{const r=await axios.post(`${API_BASE}/toggle-auto-purchase`,{},{headers:getAuthHeaders()});if(r.data.success){setAutoPurchase(r.data.auto_purchase);setNotification({type:'success',message:r.data.message});}}catch(e){setNotification({type:'error',message:'Ошибка'});} };
    const handleManualReinvest = async (id) => { try{const r=await axios.post(`${API_BASE}/manual-reinvest`,{mentor_position_id:id},{headers:getAuthHeaders()});setNotification({type:r.data.success?'success':'error',message:r.data.message});if(r.data.success)fetchData(false);}catch(e){setNotification({type:'error',message:'Ошибка'});} };


    const CaseTab = ({ level }) => { const c=CASE_CONFIG[level]; const a=activeCase===level; const mainCnt=(fullMainQueues[level]||[]).length; const pendCnt=(fullPendingQueues[level]||[]).length; return (
        <button onClick={()=>setActiveCase(level)} className={`flex-1 py-2.5 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${a?`bg-gradient-to-r ${c.gradient} text-white shadow-md`:'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <div className="font-semibold">{c.name}</div>
            <div className={`text-[10px] ${a?'text-white/80':'text-gray-400'}`}>{mainCnt}{pendCnt>0?`+${pendCnt}`:''} • ${c.cost}→${c.exit}</div>
        </button>); };


    if (loading) return (<div className="flex items-center justify-center min-h-[60vh]"><div className="text-center"><Loader className="animate-spin mx-auto mb-3 text-orange-500" size={32}/><p className="text-gray-500 text-sm">Загрузка Turbo Keys...</p></div></div>);

    const cb = statistics.case_breakdown || {};

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6">
            {/* Шапка */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                    <button onClick={onBack||(()=>navigate('/clusters'))} className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"><ChevronsLeft size={20} className="text-gray-500"/></button>
                    <div>
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">Light Keys Turbo</h1>
                        <p className="text-xs sm:text-sm text-gray-500">3 кейса • Вход $100 → Выход $2 700</p>
                    </div>
                </div>
                <button onClick={()=>fetchData(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors"><RefreshCw size={18} className="text-gray-400"/></button>
            </div>

            {notification && (<div className={`mb-4 p-3 rounded-lg text-sm ${notification.type==='success'?'bg-green-50 text-green-700 border border-green-200':'bg-red-50 text-red-700 border border-red-200'}`}>{notification.message}</div>)}

            {isProcessing && purchaseStatus && purchaseCountdown <= 0 && (
                <div className="mb-4 bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl p-4 animate-pulse">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center"><Loader className="animate-spin mr-2 text-orange-500" size={20}/><span className="font-semibold text-orange-700">Покупка обрабатывается...</span></div>
                        {processingTime > 0 && <span className="font-mono font-semibold text-orange-600">{Math.floor(processingTime/60)}:{(processingTime%60).toString().padStart(2,'0')}</span>}
                    </div>
                    <div className="bg-white/50 rounded-lg p-3"><MicrosecondsDisplay dateString={purchaseStatus.clicked_at}/></div>
                    <div className="mt-2 text-xs text-gray-600">Обработка обычно занимает 1-3 минуты.</div>
                </div>
            )}

            {/* Статистика */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="bg-orange-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5"><Users size={14} className="text-orange-500 mr-1.5"/><span className="text-[10px] sm:text-xs font-medium text-gray-700">В очереди</span></div>
                    <div className="space-y-0.5">
                        {[1,2,3].map(lv => { const c=cb[lv]||{}; return (
                            <div key={lv} className="flex justify-between items-center">
                                <span className={`text-[9px] font-medium bg-gradient-to-r ${CASE_CONFIG[lv].gradient} bg-clip-text text-transparent`}>К{lv}</span>
                                <span className="text-xs font-bold text-orange-500">{c.total_in_queue || 0}</span>
                            </div>
                        ); })}
                    </div>
                </div>
                <div className="bg-orange-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5"><User size={14} className="text-orange-500 mr-1.5"/><span className="text-[10px] sm:text-xs font-medium text-gray-700">Ваши места</span></div>
                    <div className="space-y-0.5">
                        {[1,2,3].map(lv => { const c=cb[lv]||{}; return (
                            <div key={lv} className="flex justify-between items-center">
                                <span className={`text-[9px] font-medium bg-gradient-to-r ${CASE_CONFIG[lv].gradient} bg-clip-text text-transparent`}>К{lv}</span>
                                <span className="text-xs font-bold text-orange-500">{c.active || 0}</span>
                            </div>
                        ); })}
                    </div>
                </div>
                <div className="bg-orange-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5"><Award size={14} className="text-orange-500 mr-1.5"/><span className="text-[10px] sm:text-xs font-medium text-gray-700">Заработано</span></div>
                    <p className="text-xl sm:text-2xl font-bold text-orange-500">${statistics.earned}</p>
                </div>
                <div className="bg-green-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5"><TrendingUp size={14} className="text-green-500 mr-1.5"/><span className="text-[10px] sm:text-xs font-medium text-gray-700">Закрыто</span></div>
                    <div className="space-y-0.5">
                        {[1,2,3].map(lv => { const c=cb[lv]||{}; return (
                            <div key={lv} className="flex justify-between items-center">
                                <span className={`text-[9px] font-medium bg-gradient-to-r ${CASE_CONFIG[lv].gradient} bg-clip-text text-transparent`}>К{lv}</span>
                                <span className="text-xs font-bold text-green-500">{c.completed || 0}</span>
                            </div>
                        ); })}
                    </div>
                </div>
                <div className="bg-pink-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5"><Heart size={14} className="text-pink-500 mr-1.5"/><span className="text-[10px] sm:text-xs font-medium text-gray-700">Благотворит.</span></div>
                    <p className="text-xl sm:text-2xl font-bold text-pink-500">{statistics.charity_queue_size || 0}</p>
                </div>
                <div className="bg-indigo-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5"><Shield size={14} className="text-indigo-500 mr-1.5"/><span className="text-[10px] sm:text-xs font-medium text-gray-700">Резервный фонд</span></div>
                    <p className="text-xl sm:text-2xl font-bold text-indigo-500">${Number(reserveFund).toLocaleString('ru-RU', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
            </div>

            {/* По кейсам — глобальная статистика */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                {[1,2,3].map(lv => { const c = cb[lv] || {}; const cfg = CASE_CONFIG[lv]; return (
                    <div key={lv} className={`${cfg.bg} rounded-xl p-3 border ${cfg.border}`}>
                        <div className={`text-[10px] font-semibold ${cfg.text} mb-2`}>{cfg.name}</div>
                        <div className="space-y-1">
                            <div className="flex justify-between items-center"><span className="text-[10px] text-gray-500">Всего</span><span className="text-sm font-bold text-gray-700">{(c.total_in_queue || 0) + (c.total_completed || 0)}</span></div>
                            <div className="flex justify-between items-center"><span className="text-[10px] text-gray-500">Ваши</span><span className="text-sm font-bold text-orange-500">{c.active || 0}</span></div>
                            <div className="flex justify-between items-center"><span className="text-[10px] text-gray-500">Закрыто</span><span className="text-sm font-bold text-green-500">{c.total_completed || 0}</span></div>
                        </div>
                    </div>
                ); })}
            </div>

            {/* Кнопка покупки */}
            <div className="mb-4 sm:mb-6">
                {purchaseCountdown > 0 && purchaseInfo?.reason === 'not_started' ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                        <p className="text-sm text-orange-700 mb-2 font-medium">Старт Turbo Keys через:</p>
                        <div className="flex justify-center gap-2">
                            {countdown.days > 0 && <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-orange-100"><span className="text-xl font-bold text-orange-600">{countdown.days}</span><span className="text-xs text-orange-400 ml-1">дн</span></div>}
                            {['hours','mins','secs'].map((k,i) => (<div key={k} className="bg-white rounded-lg px-3 py-2 shadow-sm border border-orange-100"><span className="text-xl font-bold text-orange-600">{countdown[k]}</span><span className="text-xs text-orange-400 ml-1">{['ч','мин','сек'][i]}</span></div>))}
                        </div>
                        <div className="mt-3 flex items-center justify-center gap-3">
                            <button onClick={syncTimer} disabled={syncing}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${syncing ? 'bg-gray-100 text-gray-400' : 'bg-white border border-orange-200 text-orange-600 hover:bg-orange-100'}`}>
                                <RefreshCw size={12} className={syncing ? 'animate-spin' : ''}/> {syncing ? 'Синхронизация...' : 'Синхронизировать'}
                            </button>
                        </div>
                        {lastSyncTime && (
                            <div className="mt-2 text-[10px] text-gray-400">
                                Серверное время: {new Date(lastSyncTime).toLocaleTimeString('ru-RU', {hour:'2-digit',minute:'2-digit',second:'2-digit', timeZone:'Europe/Moscow'})} МСК
                            </div>
                        )}

                        {/* Счётчик и кнопка готовности */}
                        {totalReady > 0 && (
                            <div className="mt-3 flex items-center justify-center gap-1.5">
                                <Users size={14} className="text-orange-600"/>
                                <span className="text-sm font-medium text-orange-700">
                                    {totalReady} {totalReady === 1 ? 'участник готов' : totalReady < 5 ? 'участника готовы' : 'участников готовы'}
                                </span>
                            </div>
                        )}
                        <div className="mt-3">
                            {isReady ? (
                                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-100 text-orange-700 font-semibold text-sm">
                                    <Award size={16}/> Вы готовы к старту!
                                </div>
                            ) : (
                                <button onClick={handleMarkReady} disabled={markingReady}
                                        className={`w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-white transition-all ${markingReady ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'}`}>
                                    {markingReady ? (
                                        <span className="flex items-center justify-center gap-2"><Loader className="animate-spin" size={16}/> Проверка...</span>
                                    ) : (
                                        <span className="flex items-center justify-center gap-2"><Zap size={16}/> Я готов участвовать!</span>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Кнопка "Жду благотворительность" */}
                        <div className="mt-3 pt-3 border-t border-orange-100">
                            {totalCharityReady > 0 && (
                                <div className="mb-2 flex items-center justify-center gap-1.5">
                                    <Heart size={14} className="text-pink-500"/>
                                    <span className="text-xs font-medium text-pink-600">
                                        {totalCharityReady} {totalCharityReady === 1 ? 'ждёт' : 'ждут'} благотворительность
                                    </span>
                                </div>
                            )}
                            {!isReady && (
                                isCharityReady ? (
                                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-100 text-pink-700 font-semibold text-xs">
                                        <Heart size={14}/> Вы в списке на благотворительность
                                    </div>
                                ) : (
                                    <button onClick={handleMarkCharityReady} disabled={markingCharityReady}
                                            className={`w-full sm:w-auto px-5 py-2 rounded-xl font-semibold text-white text-xs transition-all ${markingCharityReady ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 shadow-md'}`}>
                                        {markingCharityReady ? (
                                            <span className="flex items-center justify-center gap-2"><Loader className="animate-spin" size={14}/> Запись...</span>
                                        ) : (
                                            <span className="flex items-center justify-center gap-2"><Heart size={14}/> Жду благотворительность</span>
                                        )}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                ) : (
                    <button onClick={handlePurchase} disabled={!canPurchase||purchasing||isProcessing}
                            className={`w-full py-3.5 px-4 rounded-xl font-semibold text-white transition-all ${!canPurchase||purchasing||isProcessing?'bg-gray-300 cursor-not-allowed':'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'}`}>
                        {purchasing ? <span className="flex items-center justify-center gap-2"><Loader className="animate-spin" size={16}/> Обработка...</span>
                            : isProcessing ? <span className="flex items-center justify-center gap-2"><Loader className="animate-spin" size={16}/> Ожидание...</span>
                                : <span className="flex items-center justify-center gap-2"><ShoppingCart size={16}/> Купить место — $100</span>}
                    </button>
                )}
                {!canPurchase && purchaseInfo?.reason && purchaseInfo.reason !== 'not_started' && <div className="mt-2 text-xs text-center text-gray-500">{purchaseInfo.message}</div>}

                {/* Глобальный тумблер автопокупки */}
                <div className="mt-3 flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                    <div>
                        <div className="text-xs font-medium text-gray-700">Автопокупка (2+ круг)</div>
                        <div className="text-[10px] text-gray-400">Автоматически покупать место при закрытии позиции 2 Кейса 3</div>
                    </div>
                    <button onClick={handleToggleAuto}>
                        {autoPurchase ? <ToggleRight size={28} className="text-orange-500"/> : <ToggleLeft size={28} className="text-gray-300"/>}
                    </button>
                </div>
            </div>

            {/* Ваши места */}
            {(userPlaces.length > 0 || pendingPlaces.length > 0) && (
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-orange-500"/>Ваши места ({userPlaces.length+pendingPlaces.length})</h2>
                    <div className="grid gap-2 sm:gap-3">
                        {userPlaces.map(place => { const cfg=CASE_CONFIG[place.level]; return (
                            <div key={`m-${place.id}`} className={`bg-white border rounded-xl p-3 sm:p-4 shadow-sm ${cfg.border}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium text-white bg-gradient-to-r ${cfg.gradient}`}>{cfg.name}</span>
                                        <span className="text-xs text-gray-500">Круг {place.round}</span>
                                        {place.closing_position && <span className="text-xs font-medium text-orange-600">#{place.closing_position} на закрытие</span>}
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${place.positions_to_complete<=1?'bg-yellow-100 text-yellow-700':'bg-gray-100 text-gray-600'}`}>{place.status}</span>
                                </div>
                                <PositionBar occupied={3-place.positions_to_complete}/>
                                <div className="flex justify-between mt-2 text-[10px] text-gray-500"><span>Осталось: {place.positions_to_complete}/3</span><span>${cfg.cost}→${cfg.exit}</span></div>
                                {place.level === 3 && !autoPurchase && place.positions_to_complete <= 1 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                        <button onClick={()=>handleManualReinvest(place.id)} className="w-full py-1.5 text-[10px] font-medium bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors">Купить место вручную ($100)</button>
                                    </div>
                                )}
                                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1.5"><Clock size={10} className="text-orange-400"/><span className="text-[10px] text-gray-400">Активация:</span><MicrosecondsDisplay dateString={place.activated_at||place.purchase_timestamp} short/></div>
                            </div>); })}
                        {pendingPlaces.map(place => (
                            <div key={`p-${place.id}`} className="bg-white border border-amber-200 rounded-xl p-3 sm:p-4 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Ожидание • {CASE_CONFIG[place.level]?.name}</span></div>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{place.source==='charity'?'Благотворит.':place.source==='case_upgrade'?'Переход':'Покупка'}</span>
                                </div>
                            </div>))}
                    </div>
                </div>
            )}

            {/* Основная очередь — табы кейсов */}
            <div className="mb-4 sm:mb-6">
                <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2"><Zap size={16} className="text-orange-500"/>Очередь</h2>
                <div className="flex gap-2 mb-3"><CaseTab level={1}/><CaseTab level={2}/><CaseTab level={3}/></div>

                {caseQueues[activeCase] && caseQueues[activeCase].length > 0 ? (<>
                    <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-3">
                        {caseQueues[activeCase].map(item => (
                            <div key={item.id} className={`bg-white rounded-xl p-3 sm:p-4 shadow-sm border ${item.isCurrentUser?'border-orange-400 ring-1 ring-orange-200':'border-gray-100'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-mono text-orange-500 font-semibold">#{item.closing_position}</span>
                                        <User size={12} className={item.isCurrentUser?'text-orange-500':'text-gray-400'}/>
                                        <span className={`text-xs font-medium ${item.isCurrentUser?'text-orange-600':'text-gray-600'}`}>{item.login}{item.isCurrentUser&&' (Вы)'}</span>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${item.remaining<=1?'bg-yellow-100 text-yellow-700':'bg-gray-100 text-gray-600'}`}>{item.status_text}</span>
                                </div>
                                <PositionBar occupied={item.occupied_count}/>
                                <div className="flex justify-between mt-1.5 text-[10px] text-gray-400"><span>{item.occupied_count}/3</span><span>Круг {item.round}</span></div>
                                {(item.activated_at||item.purchase_timestamp) && (<div className="mt-2 pt-2 border-t border-gray-100"><MicrosecondsDisplay dateString={item.activated_at||item.purchase_timestamp} short/></div>)}
                            </div>))}
                    </div>

                    {/* Полная основная очередь текущего кейса */}
                    {(fullMainQueues[activeCase]||[]).length > 0 && (<>
                        <button onClick={()=>setShowFullQueue(p=>({...p,[activeCase]:!p[activeCase]}))} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-orange-600 transition-colors mb-2">
                            <Grid size={14}/><span>Основная {CASE_CONFIG[activeCase].name} ({(fullMainQueues[activeCase]||[]).length})</span>{showFullQueue[activeCase]?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                        </button>
                        {showFullQueue[activeCase] && <QueueTable data={fullMainQueues[activeCase]} level={activeCase}/>}
                    </>)}
                </>) : (<div className="text-center py-8 text-gray-400 text-sm">Основная очередь {CASE_CONFIG[activeCase].name} пока пуста</div>)}

                {/* Предварительная очередь текущего кейса */}
                {(fullPendingQueues[activeCase]||[]).length > 0 && (
                    <div className="mt-3">
                        <button onClick={()=>setShowPendingQueue(p=>({...p,[activeCase]:!p[activeCase]}))} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-amber-600 transition-colors mb-2">
                            <List size={14} className="text-amber-500"/><span>Предварительная {CASE_CONFIG[activeCase].name} ({(fullPendingQueues[activeCase]||[]).length})</span>{showPendingQueue[activeCase]?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                        </button>
                        {showPendingQueue[activeCase] && <PendingQueueTable data={fullPendingQueues[activeCase]||[]}/>}
                    </div>
                )}
            </div>

            {/* Благотворительная очередь */}
            <div className="mb-4 sm:mb-6">
                <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2"><Heart size={16} className="text-pink-500"/>Благотворительная очередь <span className="text-xs font-normal text-gray-400">({charityQueue.total_waiting} чел.)</span></h2>
                <div className="bg-white border border-pink-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-3">Бесплатная очередь. При активации — автоматический переход в Кейс 1.</p>
                    {charityQueue.is_in_queue ? (
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-pink-600">Вы в очереди • Позиция #{charityQueue.user_position}</span>
                            <button onClick={handleDeclineCharity} disabled={decliningCharity} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"><XCircle size={14}/>{decliningCharity?'Отмена...':'Отказаться'}</button>
                        </div>
                    ) : purchaseCountdown > 0 && purchaseInfo?.reason === 'not_started' ? (
                        <div className="text-xs text-gray-400 text-center py-2">Запись откроется после старта системы</div>
                    ) : charityQueue.can_join ? (
                        <button onClick={handleJoinCharity} disabled={joiningCharity} className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md ${joiningCharity?'bg-gray-300 cursor-not-allowed':'bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:from-pink-600 hover:to-rose-700'}`}>
                            {joiningCharity ? <span className="flex items-center justify-center gap-2"><Loader className="animate-spin" size={16}/> Записываем...</span>
                                : <span className="flex items-center justify-center gap-2"><Heart size={16}/>Записаться бесплатно</span>}
                        </button>
                    ) : (
                        <div className="text-xs text-gray-400 text-center py-2">{(userPlaces.length>0||pendingPlaces.length>0)?'Вы уже в основной очереди':'Недоступно'}</div>
                    )}

                    {fullCharityQueue.length > 0 && (
                        <div className="mt-3">
                            <button onClick={()=>setShowCharityList(!showCharityList)} className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-pink-600 transition-colors">
                                <span>Все в очереди ({fullCharityQueue.length})</span>{showCharityList?<ChevronUp size={12}/>:<ChevronDown size={12}/>}
                            </button>
                            {showCharityList && <CharityQueueList data={fullCharityQueue}/>}
                        </div>
                    )}
                </div>
            </div>

            {/* Как это работает */}
            <div className="mb-4">
                <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2"><Rocket size={16} className="text-orange-500"/>Как это работает</h2>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2">
                        {[1,2,3].map(lv => { const c=CASE_CONFIG[lv]; return (
                            <div key={lv} className="flex-1 flex items-center gap-2">
                                <div className="text-center flex-1">
                                    <div className={`w-10 h-10 mx-auto mb-1 rounded-xl bg-gradient-to-br ${c.gradient} flex items-center justify-center shadow-sm`}><span className="text-white font-bold text-sm">{lv}</span></div>
                                    <div className={`text-xs font-semibold bg-gradient-to-r ${c.gradient} bg-clip-text text-transparent`}>{c.name}</div>
                                    <div className="text-[10px] text-gray-500">${c.cost}→${c.exit}</div>
                                    <div className="text-[10px] text-gray-400">3 позиции</div>
                                </div>
                                {lv < 3 && <ArrowRight size={14} className="text-gray-300 flex-shrink-0"/>}
                            </div>); })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 text-[10px] text-gray-400 text-center">Кейс 1 → Кейс 2 → Кейс 3 → Новый круг. Благотворительная очередь → бесплатный вход в Кейс 1.</div>
                </div>
            </div>
        </div>
    );
};

export default LightTurboKeysCluster;
